import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

interface UserProfile {
  id: string;
  name: string;
  email: string;
  organization: string;
  status: string;
  createdAt: string;
}

interface PortvilleApplication {
  id: string;
  name: string;
  organization: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: any;
}

interface LevelUpRequest {
  id: string;
  uid: string;
  name: string;
  email: string;
  organization: string;
  currentLevel: number;
  targetLevel: number;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: any;
}

interface Reservation {
  id: string;
  facilityName: string;
  name: string;
  reservationDate: Date;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
}

interface RecentPortvilleStayRequest {
  id: string;
  type: 'extension' | 'moveOut';
  roomId?: string;
  roomNumber: string;
  residentInfo: {
    name: string;
    organization: string;
    phoneNumber: string;
  };
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'applied' | 'contracted' | 'closed';
  appliedAt: Date;
  extensionRound?: 1 | 2;
  moveOutReason?: string;
  desiredMoveOutDate?: Date;
}

interface RecentCustomerInquiry {
  id: string;
  userInfo: {
    name: string;
    email: string;
    organization: string;
  };
  category: 'general' | 'portville' | 'parking' | 'facility' | 'account';
  subject: string;
  status: 'pending' | 'answered' | 'closed';
  priority: 'low' | 'normal' | 'high';
  createdAt: Date;
}

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  waitingUsers: number;
  pendingLevelUpUsers: number;
  recentUsers: UserProfile[];
  recentLevelUpRequests: LevelUpRequest[];
  recentPortvilleApplications: PortvilleApplication[];
  recentFacilityReservations: Reservation[];
  recentPortvilleStayRequests: RecentPortvilleStayRequest[];
  recentCustomerInquiries: RecentCustomerInquiry[];
  isLoading: boolean;
  error: string | null;
}

const useAdminStats = (): DashboardStats => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeUsers: 0,
    waitingUsers: 0,
    pendingLevelUpUsers: 0,
    recentUsers: [],
    recentLevelUpRequests: [],
    recentPortvilleApplications: [],
    recentFacilityReservations: [],
    recentPortvilleStayRequests: [],
    recentCustomerInquiries: [],
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let recentMoveOutRequests: RecentPortvilleStayRequest[] = [];
    let recentExtensionRequests: RecentPortvilleStayRequest[] = [];
    let recentSecondExtensionRequests: RecentPortvilleStayRequest[] = [];

    const toDate = (value: any): Date | undefined => {
      if (!value) return undefined;
      return typeof value.toDate === 'function' ? value.toDate() : value;
    };

    const updateRecentPortvilleStayRequests = () => {
      const recentPortvilleStayRequests = [
        ...recentMoveOutRequests,
        ...recentExtensionRequests,
        ...recentSecondExtensionRequests,
      ]
        .filter((request) => request.appliedAt)
        .sort((a, b) => b.appliedAt.getTime() - a.appliedAt.getTime())
        .slice(0, 5);

      setStats(prev => ({
        ...prev,
        recentPortvilleStayRequests,
      }));
    };

    // 사용자 통계 실시간 구독
    const usersUnsubscribe = onSnapshot(
      collection(db, 'user_profiles'),
      (snapshot) => {
        const users = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as UserProfile[];
        
        const totalUsers = users.length;
        const activeUsers = users.filter(user => user.status === 'active').length;
        const waitingUsers = users.filter(user => user.status === 'pending').length;

        setStats(prev => ({
          ...prev,
          totalUsers,
          activeUsers,
          waitingUsers,
        }));
      },
      (error) => {
        console.error('사용자 통계 실시간 업데이트 오류:', error);
        setStats(prev => ({
          ...prev,
          error: '사용자 통계 업데이트 중 오류가 발생했습니다.',
        }));
      }
    );

    // 등업 신청 실시간 구독
    const levelUpUnsubscribe = onSnapshot(
      query(
        collection(db, 'levelup_requests'),
        where('status', '==', 'pending')
      ),
      (snapshot) => {
        setStats(prev => ({
          ...prev,
          pendingLevelUpUsers: snapshot.size,
        }));
      },
      (error) => {
        console.error('등업 신청 실시간 업데이트 오류:', error);
      }
    );

    // 최근 등업 신청 실시간 구독
    const recentLevelUpUnsubscribe = onSnapshot(
      query(
        collection(db, 'levelup_requests'),
        orderBy('createdAt', 'desc'),
        limit(5)
      ),
      (snapshot) => {
        const recentLevelUpRequests = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            uid: data.uid || doc.id,
            ...data
          } as LevelUpRequest;
        });

        setStats(prev => ({
          ...prev,
          recentLevelUpRequests,
        }));
      },
      (error) => {
        console.error('최근 등업 신청 실시간 업데이트 오류:', error);
      }
    );

    // 최근 사용자 실시간 구독
    const recentUsersUnsubscribe = onSnapshot(
      query(
        collection(db, 'user_profiles'),
        orderBy('createdAt', 'desc'),
        limit(5)
      ),
      (snapshot) => {
        const recentUsers = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as UserProfile[];

        setStats(prev => ({
          ...prev,
          recentUsers,
        }));
      },
      (error) => {
        console.error('최근 사용자 실시간 업데이트 오류:', error);
      }
    );

    // 최근 포트빌 신청 실시간 구독
    const portvilleUnsubscribe = onSnapshot(
      query(
        collection(db, 'portville_applications'),
        orderBy('createdAt', 'desc'),
        limit(5)
      ),
      (snapshot) => {
        const recentPortvilleApplications = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as PortvilleApplication[];

        setStats(prev => ({
          ...prev,
          recentPortvilleApplications,
        }));
      },
      (error) => {
        console.error('최근 포트빌 신청 실시간 업데이트 오류:', error);
      }
    );

    // 최근 시설물 예약 실시간 구독
    const reservationsUnsubscribe = onSnapshot(
      query(
        collection(db, 'reservations'),
        orderBy('createdAt', 'desc'),
        limit(5)
      ),
      (snapshot) => {
        const recentFacilityReservations = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            facilityName: data.facilityName,
            name: data.name,
            reservationDate: data.reservationDate?.toDate(),
            status: data.status,
          } as Reservation;
        });

        setStats(prev => ({
          ...prev,
          recentFacilityReservations,
        }));
      },
      (error) => {
        console.error('최근 시설물 예약 실시간 업데이트 오류:', error);
      }
    );

    // 최근 퇴거 신청 실시간 구독
    const moveOutRequestsUnsubscribe = onSnapshot(
      query(
        collection(db, 'portville_move_out_requests'),
        orderBy('appliedAt', 'desc'),
        limit(5)
      ),
      (snapshot) => {
        recentMoveOutRequests = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            type: 'moveOut',
            roomId: data.roomId,
            roomNumber: data.roomNumber,
            residentInfo: data.residentInfo,
            moveOutReason: data.moveOutReason,
            desiredMoveOutDate: toDate(data.desiredMoveOutDate),
            status: data.status,
            appliedAt: toDate(data.appliedAt),
          } as RecentPortvilleStayRequest;
        });

        updateRecentPortvilleStayRequests();
        setStats(prev => ({
          ...prev,
          isLoading: false,
        }));
      },
      (error) => {
        console.error('최근 퇴거 신청 실시간 업데이트 오류:', error);
        setStats(prev => ({
          ...prev,
          isLoading: false,
          error: '퇴거 신청 업데이트 중 오류가 발생했습니다.',
        }));
      }
    );

    // 최근 1차 연장 신청 실시간 구독
    const extensionRequestsUnsubscribe = onSnapshot(
      query(
        collection(db, 'portville_residencies'),
        orderBy('extensionAppliedAt', 'desc'),
        limit(5)
      ),
      (snapshot) => {
        recentExtensionRequests = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: `${doc.id}-extension-1`,
            type: 'extension',
            roomId: data.roomId,
            roomNumber: data.roomNumber,
            residentInfo: data.residentInfo,
            status: data.extensionStatus || 'applied',
            appliedAt: toDate(data.extensionAppliedAt),
            extensionRound: 1,
          } as RecentPortvilleStayRequest;
        });

        updateRecentPortvilleStayRequests();
      },
      (error) => {
        console.error('최근 1차 연장 신청 실시간 업데이트 오류:', error);
      }
    );

    // 최근 2차 연장 신청 실시간 구독
    const secondExtensionRequestsUnsubscribe = onSnapshot(
      query(
        collection(db, 'portville_residencies'),
        orderBy('secondExtensionAppliedAt', 'desc'),
        limit(5)
      ),
      (snapshot) => {
        recentSecondExtensionRequests = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: `${doc.id}-extension-2`,
            type: 'extension',
            roomId: data.roomId,
            roomNumber: data.roomNumber,
            residentInfo: data.residentInfo,
            status: data.secondExtensionStatus || 'applied',
            appliedAt: toDate(data.secondExtensionAppliedAt),
            extensionRound: 2,
          } as RecentPortvilleStayRequest;
        });

        updateRecentPortvilleStayRequests();
      },
      (error) => {
        console.error('최근 2차 연장 신청 실시간 업데이트 오류:', error);
      }
    );

    // 최근 고객문의 실시간 구독
    const customerInquiriesUnsubscribe = onSnapshot(
      query(
        collection(db, 'customer_inquiries'),
        orderBy('createdAt', 'desc'),
        limit(5)
      ),
      (snapshot) => {
        const recentCustomerInquiries = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            userInfo: data.userInfo,
            category: data.category,
            subject: data.subject,
            status: data.status,
            priority: data.priority,
            createdAt: toDate(data.createdAt),
          } as RecentCustomerInquiry;
        });

        setStats(prev => ({
          ...prev,
          recentCustomerInquiries,
        }));
      },
      (error) => {
        console.error('최근 고객문의 실시간 업데이트 오류:', error);
      }
    );

    // 컴포넌트 언마운트 시 구독 해제
    return () => {
      usersUnsubscribe();
      levelUpUnsubscribe();
      recentLevelUpUnsubscribe();
      recentUsersUnsubscribe();
      portvilleUnsubscribe();
      reservationsUnsubscribe();
      moveOutRequestsUnsubscribe();
      extensionRequestsUnsubscribe();
      secondExtensionRequestsUnsubscribe();
      customerInquiriesUnsubscribe();
    };
  }, []);

  return stats;
};

export default useAdminStats;
